import { useMemo, useState } from "react";

import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";

import GameFields from "../shared/GameFields";
import resolveGameConfig from "../shared/resolveGameConfig";

import {
  calculateProgress,
  calculateAccuracy,
  calculateAverageReactionTime,
  calculateTotalTime,
  calculateErrorCount,
} from "../shared/gameMetrics";

import "./PatternMatrices.css";

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalRounds: 8,
    itemCount: 3,
    patternLength: 3,
    allowedTypes: [
      "alternating",
      "rotation",
      "quantity",
      "arithmetic",
    ],
  },

  medium: {
    label: "Media",
    totalRounds: 10,
    itemCount: 4,
    patternLength: 4,
    allowedTypes: [
      "alternating",
      "rotation",
      "quantity",
      "arithmetic",
      "cycle",
      "position",
    ],
  },

  hard: {
    label: "Difícil",
    totalRounds: 12,
    itemCount: 5,
    patternLength: 5,
    allowedTypes: [
      "rotation",
      "quantity",
      "arithmetic",
      "cycle",
      "position",
      "combined",
    ],
  },
};

const SHAPES = [
  "●",
  "■",
  "▲",
  "◆",
  "★",
  "⬟",
];

const OUTLINED_SHAPES = [
  "○",
  "□",
  "△",
  "◇",
  "☆",
  "⬡",
];

const ROTATIONS = ["↑", "→", "↓", "←"];

const COLORS = [
  "matrix-token-green",
  "matrix-token-orange",
  "matrix-token-blue",
  "matrix-token-red",
  "matrix-token-purple",
];

const getRandomInteger = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const getRandomItem = (items) =>
  items[getRandomInteger(0, items.length - 1)];

const shuffleArray = (items) => {
  const shuffled = [...items];

  for (
    let index = shuffled.length - 1;
    index > 0;
    index -= 1
  ) {
    const randomIndex = Math.floor(
      Math.random() * (index + 1)
    );

    [shuffled[index], shuffled[randomIndex]] = [
      shuffled[randomIndex],
      shuffled[index],
    ];
  }

  return shuffled;
};

const createToken = ({
  content,
  color = "matrix-token-green",
  layout = "single",
  cells = null,
}) => ({
  content: String(content),
  color,
  layout,
  cells,
});

const tokenSignature = (token) =>
  JSON.stringify({
    content: token.content,
    color: token.color,
    layout: token.layout,
    cells: token.cells,
  });

const createOptions = ({
  answer,
  distractors,
  itemCount,
}) => {
  const uniqueTokens = [];
  const usedSignatures = new Set();

  [answer, ...distractors].forEach((token) => {
    const signature = tokenSignature(token);

    if (!usedSignatures.has(signature)) {
      usedSignatures.add(signature);
      uniqueTokens.push(token);
    }
  });

  let fallbackIndex = 0;

  while (uniqueTokens.length < itemCount) {
    const fallback = createToken({
      content: SHAPES[
        fallbackIndex % SHAPES.length
      ],
      color:
        COLORS[
          fallbackIndex % COLORS.length
        ],
    });

    const signature = tokenSignature(fallback);

    if (!usedSignatures.has(signature)) {
      usedSignatures.add(signature);
      uniqueTokens.push(fallback);
    }

    fallbackIndex += 1;
  }

  return shuffleArray(
    uniqueTokens.slice(0, itemCount).map(
      (token, index) => ({
        ...token,
        id: `${Date.now()}-${index}-${Math.random()}`,
        isCorrect:
          tokenSignature(token) ===
          tokenSignature(answer),
      })
    )
  );
};

const createAlternatingRound = ({
  patternLength,
  itemCount,
}) => {
  const shapeIndex = getRandomInteger(
    0,
    SHAPES.length - 1
  );

  const firstToken = createToken({
    content: SHAPES[shapeIndex],
    color: getRandomItem(COLORS),
  });

  const secondToken = createToken({
    content: OUTLINED_SHAPES[shapeIndex],
    color: getRandomItem(
      COLORS.filter(
        (color) => color !== firstToken.color
      )
    ),
  });

  const fullSequence = Array.from(
    { length: patternLength + 1 },
    (_, index) =>
      index % 2 === 0
        ? firstToken
        : secondToken
  );

  const answer =
    fullSequence[fullSequence.length - 1];

  const distractors = [
    answer === firstToken
      ? secondToken
      : firstToken,

    createToken({
      content: SHAPES[
        (shapeIndex + 1) % SHAPES.length
      ],
      color: answer.color,
    }),

    createToken({
      content: answer.content,
      color: getRandomItem(
        COLORS.filter(
          (color) => color !== answer.color
        )
      ),
    }),

    createToken({
      content:
        answer.content === SHAPES[shapeIndex]
          ? OUTLINED_SHAPES[shapeIndex]
          : SHAPES[shapeIndex],
      color: getRandomItem(COLORS),
    }),
  ];

  return {
    type: "alternating",
    ruleLabel: "Alternancia",
    sequence: fullSequence.slice(0, -1),
    answer,
    options: createOptions({
      answer,
      distractors,
      itemCount,
    }),
  };
};

const createRotationRound = ({
  patternLength,
  itemCount,
}) => {
  const startIndex = getRandomInteger(0, 3);

  const direction =
    Math.random() < 0.5 ? 1 : -1;

  const fullSequence = Array.from(
    { length: patternLength + 1 },
    (_, index) => {
      const rotationIndex =
        (startIndex +
          direction * index +
          ROTATIONS.length * 10) %
        ROTATIONS.length;

      return createToken({
        content: ROTATIONS[rotationIndex],
        color: "matrix-token-green",
      });
    }
  );

  const answer =
    fullSequence[fullSequence.length - 1];

  const distractors = ROTATIONS.filter(
    (rotation) => rotation !== answer.content
  ).map((rotation, index) =>
    createToken({
      content: rotation,
      color:
        index === 0
          ? "matrix-token-green"
          : COLORS[(index + 1) % COLORS.length],
    })
  );

  distractors.push(
    createToken({
      content: answer.content,
      color: "matrix-token-orange",
    })
  );

  return {
    type: "rotation",
    ruleLabel: "Rotación",
    sequence: fullSequence.slice(0, -1),
    answer,
    options: createOptions({
      answer,
      distractors,
      itemCount,
    }),
  };
};

const createQuantityRound = ({
  patternLength,
  itemCount,
}) => {
  const symbol = getRandomItem([
    "●",
    "■",
    "▲",
    "★",
  ]);

  const startAmount = getRandomInteger(1, 2);
  const increment =
    Math.random() < 0.75 ? 1 : 2;

  const fullSequence = Array.from(
    { length: patternLength + 1 },
    (_, index) =>
      createToken({
        content: symbol.repeat(
          startAmount + index * increment
        ),
        color: getRandomItem(COLORS.slice(0, 3)),
        layout: "quantity",
      })
  );

  const answer =
    fullSequence[fullSequence.length - 1];

  const correctAmount =
    startAmount + patternLength * increment;

  const distractorAmounts = [
    Math.max(1, correctAmount - 1),
    correctAmount + 1,
    Math.max(1, correctAmount - increment),
    correctAmount + increment,
    Math.max(1, correctAmount - 2),
  ];

  const distractors = distractorAmounts.map(
    (amount, index) =>
      createToken({
        content: symbol.repeat(amount),
        color:
          index < 2
            ? answer.color
            : COLORS[
                (COLORS.indexOf(answer.color) +
                  index +
                  1) %
                  COLORS.length
              ],
        layout: "quantity",
      })
  );

  return {
    type: "quantity",
    ruleLabel: "Cambio de cantidad",
    sequence: fullSequence.slice(0, -1),
    answer,
    options: createOptions({
      answer,
      distractors,
      itemCount,
    }),
  };
};

const createArithmeticRound = ({
  patternLength,
  itemCount,
}) => {
  const start = getRandomInteger(1, 15);
  const increment = getRandomItem([
    2,
    3,
    4,
    5,
  ]);

  const fullSequence = Array.from(
    { length: patternLength + 1 },
    (_, index) =>
      createToken({
        content: start + index * increment,
        color: "matrix-token-number",
      })
  );

  const answer =
    fullSequence[fullSequence.length - 1];

  const answerValue = Number(answer.content);

  const distractors = [
    answerValue - 1,
    answerValue + 1,
    answerValue - increment,
    answerValue + increment,
    answerValue + 2,
  ].map((value) =>
    createToken({
      content: value,
      color: "matrix-token-number",
    })
  );

  return {
    type: "arithmetic",
    ruleLabel: "Progresión numérica",
    sequence: fullSequence.slice(0, -1),
    answer,
    options: createOptions({
      answer,
      distractors,
      itemCount,
    }),
  };
};

const createCycleRound = ({
  patternLength,
  itemCount,
}) => {
  const shuffledShapes = shuffleArray(SHAPES);
  const cycleLength =
    patternLength >= 5 ? 3 : 2;

  const cycle = shuffledShapes
    .slice(0, cycleLength)
    .map((shape, index) =>
      createToken({
        content: shape,
        color: COLORS[index],
      })
    );

  const fullSequence = Array.from(
    { length: patternLength + 1 },
    (_, index) => cycle[index % cycle.length]
  );

  const answer =
    fullSequence[fullSequence.length - 1];

  const distractors = [
    ...cycle.filter(
      (token) =>
        tokenSignature(token) !==
        tokenSignature(answer)
    ),

    createToken({
      content: answer.content,
      color: getRandomItem(
        COLORS.filter(
          (color) => color !== answer.color
        )
      ),
    }),

    createToken({
      content: getRandomItem(
        SHAPES.filter(
          (shape) => shape !== answer.content
        )
      ),
      color: answer.color,
    }),

    createToken({
      content: getRandomItem(OUTLINED_SHAPES),
      color: answer.color,
    }),
  ];

  return {
    type: "cycle",
    ruleLabel: "Ciclo de elementos",
    sequence: fullSequence.slice(0, -1),
    answer,
    options: createOptions({
      answer,
      distractors,
      itemCount,
    }),
  };
};

const createPositionToken = ({
  activeIndex,
  secondActiveIndex = null,
  color = "matrix-token-green",
}) =>
  createToken({
    content: "",
    color,
    layout: "grid",
    cells: Array.from(
      { length: 9 },
      (_, index) =>
        index === activeIndex ||
        index === secondActiveIndex
    ),
  });

const createPositionRound = ({
  patternLength,
  itemCount,
}) => {
  const route = [
    0,
    1,
    2,
    5,
    8,
    7,
    6,
    3,
  ];

  const startIndex = getRandomInteger(
    0,
    route.length - 1
  );

  const fullSequence = Array.from(
    { length: patternLength + 1 },
    (_, index) =>
      createPositionToken({
        activeIndex:
          route[
            (startIndex + index) %
              route.length
          ],
      })
  );

  const answer =
    fullSequence[fullSequence.length - 1];

  const currentAnswerPosition =
    answer.cells.findIndex(Boolean);

  const distractorPositions = shuffleArray(
    Array.from(
      { length: 9 },
      (_, index) => index
    ).filter(
      (index) =>
        index !== currentAnswerPosition
    )
  ).slice(0, Math.max(itemCount - 1, 4));

  const distractors = distractorPositions.map(
    (activeIndex) =>
      createPositionToken({
        activeIndex,
      })
  );

  return {
    type: "position",
    ruleLabel: "Desplazamiento espacial",
    sequence: fullSequence.slice(0, -1),
    answer,
    options: createOptions({
      answer,
      distractors,
      itemCount,
    }),
  };
};

const createCombinedRound = ({
  patternLength,
  itemCount,
}) => {
  const route = [0, 2, 8, 6];
  const symbols = ["●", "○"];

  const startIndex = getRandomInteger(0, 3);

  const fullSequence = Array.from(
    { length: patternLength + 1 },
    (_, index) => {
      const activeIndex =
        route[
          (startIndex + index) %
            route.length
        ];

      const symbol =
        symbols[index % symbols.length];

      return createToken({
        content: symbol,
        color:
          index % 2 === 0
            ? "matrix-token-green"
            : "matrix-token-orange",
        layout: "grid-symbol",
        cells: Array.from(
          { length: 9 },
          (_, cellIndex) =>
            cellIndex === activeIndex
              ? symbol
              : ""
        ),
      });
    }
  );

  const answer =
    fullSequence[fullSequence.length - 1];

  const correctPosition =
    answer.cells.findIndex(
      (cell) => cell !== ""
    );

  const distractors = [
    createToken({
      ...answer,
      color:
        answer.color ===
        "matrix-token-green"
          ? "matrix-token-orange"
          : "matrix-token-green",
    }),

    createToken({
      content:
        answer.content === "●" ? "○" : "●",
      color: answer.color,
      layout: "grid-symbol",
      cells: answer.cells.map((cell) =>
        cell
          ? answer.content === "●"
            ? "○"
            : "●"
          : ""
      ),
    }),

    ...shuffleArray(
      route.filter(
        (position) =>
          position !== correctPosition
      )
    ).map((position) =>
      createToken({
        content: answer.content,
        color: answer.color,
        layout: "grid-symbol",
        cells: Array.from(
          { length: 9 },
          (_, index) =>
            index === position
              ? answer.content
              : ""
        ),
      })
    ),
  ];

  return {
    type: "combined",
    ruleLabel:
      "Cambio combinado de posición y forma",
    sequence: fullSequence.slice(0, -1),
    answer,
    options: createOptions({
      answer,
      distractors,
      itemCount,
    }),
  };
};

const ROUND_CREATORS = {
  alternating: createAlternatingRound,
  rotation: createRotationRound,
  quantity: createQuantityRound,
  arithmetic: createArithmeticRound,
  cycle: createCycleRound,
  position: createPositionRound,
  combined: createCombinedRound,
};

const createRound = ({
  itemCount,
  patternLength,
  allowedTypes,
  previousType = null,
}) => {
  const safeItemCount = Math.min(
    Math.max(Number(itemCount) || 3, 2),
    6
  );

  const safePatternLength = Math.min(
    Math.max(
      Number(patternLength) || 3,
      3
    ),
    6
  );

  const possibleTypes = (
    Array.isArray(allowedTypes) &&
    allowedTypes.length > 0
      ? allowedTypes
      : GAME_CONFIG.easy.allowedTypes
  ).filter(
    (type) =>
      type !== previousType ||
      allowedTypes.length === 1
  );

  const selectedType = getRandomItem(
    possibleTypes.length > 0
      ? possibleTypes
      : allowedTypes
  );

  const creator =
    ROUND_CREATORS[selectedType] ||
    createAlternatingRound;

  return {
    id: `${Date.now()}-${Math.random()}`,
    ...creator({
      itemCount: safeItemCount,
      patternLength: safePatternLength,
    }),
  };
};

function PatternToken({
  token,
  compact = false,
}) {
  if (!token) {
    return null;
  }

  if (token.layout === "grid") {
    return (
      <div
        className={`pattern-matrices-mini-grid ${
          compact
            ? "pattern-matrices-mini-grid-compact"
            : ""
        }`}
      >
        {token.cells.map(
          (isActive, index) => (
            <span
              key={index}
              className={
                isActive
                  ? token.color
                  : ""
              }
            />
          )
        )}
      </div>
    );
  }

  if (token.layout === "grid-symbol") {
    return (
      <div
        className={`pattern-matrices-symbol-grid ${
          compact
            ? "pattern-matrices-symbol-grid-compact"
            : ""
        }`}
      >
        {token.cells.map((cell, index) => (
          <span
            key={index}
            className={
              cell ? token.color : ""
            }
          >
            {cell}
          </span>
        ))}
      </div>
    );
  }

  return (
    <span
      className={`pattern-matrices-token-content ${
        token.color
      } ${
        token.layout === "quantity"
          ? "pattern-matrices-quantity"
          : ""
      }`}
    >
      {token.content}
    </span>
  );
}

function PatternMatrices() {
  const [selectedDifficulty, setSelectedDifficulty] =
    useState(null);

  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);

  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [correctAnswers, setCorrectAnswers] =
    useState(0);

  const [errors, setErrors] = useState(0);
  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  const [roundStartTime, setRoundStartTime] =
    useState(null);

  const [responseLocked, setResponseLocked] =
    useState(false);

  const totalRounds = config?.totalRounds || 0;

  const progress = useMemo(
    () => calculateProgress(roundIndex, totalRounds),
    [roundIndex, totalRounds]
  );

  const totalAnswers = correctAnswers + errors;

  const accuracy = calculateAccuracy(
    correctAnswers,
    totalAnswers
  );

  const totalErrors =
    calculateErrorCount(errors);

  const averageReactionTime =
    calculateAverageReactionTime(responses);

  const totalTime = calculateTotalTime(
    startTime,
    endTime
  );

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors: totalErrors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const startGame = (
    difficultyOrConfig =
      selectedDifficulty || "easy"
  ) => {
    const {
      difficultyKey,
      config: resolvedConfig,
    } = resolveGameConfig(
      difficultyOrConfig,
      GAME_CONFIG
    );

    const nextConfig = {
      ...resolvedConfig,
      difficultyKey,
      totalRounds:
        Number(resolvedConfig.totalRounds) ||
        8,
      itemCount: Math.min(
        Math.max(
          Number(resolvedConfig.itemCount) ||
            3,
          2
        ),
        6
      ),
      patternLength: Math.min(
        Math.max(
          Number(
            resolvedConfig.patternLength
          ) || 3,
          3
        ),
        6
      ),
      allowedTypes:
        resolvedConfig.allowedTypes ||
        GAME_CONFIG.easy.allowedTypes,
    };

    const firstRound =
      createRound(nextConfig);

    const now = Date.now();

    setSelectedDifficulty(difficultyKey);
    setConfig(nextConfig);

    setRoundIndex(0);
    setRound(firstRound);

    setStarted(true);
    setFinished(false);

    setCorrectAnswers(0);
    setErrors(0);
    setResponses([]);

    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);

    setResponseLocked(false);
  };

  const finishGame = () => {
    setEndTime(Date.now());
    setStarted(false);
    setFinished(true);
  };

  const moveToNextRound = () => {
    const nextRoundIndex = roundIndex + 1;

    if (nextRoundIndex >= totalRounds) {
      finishGame();
      return;
    }

    const nextRound = createRound({
      ...config,
      previousType: round.type,
    });

    setRoundIndex(nextRoundIndex);
    setRound(nextRound);
    setRoundStartTime(Date.now());
    setResponseLocked(false);
  };

  const handleAnswer = (option) => {
    if (!round || responseLocked) {
      return;
    }

    setResponseLocked(true);

    const responseTime =
      Date.now() - roundStartTime;

    setResponses((current) => [
      ...current,
      responseTime,
    ]);

    if (option.isCorrect) {
      setCorrectAnswers(
        (current) => current + 1
      );
    } else {
      setErrors((current) => current + 1);
    }

    moveToNextRound();
  };

  if (!started && !finished) {
    return (
      <GameLauncher
        title="Matrices y series"
        description="Observa la secuencia y selecciona el elemento que continúa correctamente el patrón."
        areas={[
          "Razonamiento",
          "Razonamiento no verbal",
          "Flexibilidad cognitiva",
          "Atención",
        ]}
        defaultDifficulty="easy"
        customFields={[
          GameFields.totalRounds({
            min: 4,
            max: 30,
            defaultValue: 10,
          }),

          GameFields.patternLength({
            label:
              "Elementos visibles de la serie",
            min: 3,
            max: 6,
            defaultValue: 4,
          }),

          GameFields.itemCount({
            label:
              "Opciones de respuesta",
            min: 2,
            max: 6,
            defaultValue: 4,
          }),
        ]}
        onStart={startGame}
      />
    );
  }

  if (finished) {
    return (
      <div className="pattern-matrices-page">
        <div className="pattern-matrices-results">
          <GameResults
            title="Resultados"
            correctAnswers={correctAnswers}
            totalAnswers={totalAnswers}
            accuracy={accuracy}
            errors={totalErrors}
            averageReactionTime={
              averageReactionTime
            }
            totalTime={totalTime}
            onRestart={() =>
              startGame(
                selectedDifficulty || "easy"
              )
            }
          />

          <div className="pattern-matrices-results-grid">
            <div className="pattern-matrices-result-card">
              <span>Aciertos</span>
              <strong>{correctAnswers}</strong>
            </div>

            <div className="pattern-matrices-result-card">
              <span>Errores</span>
              <strong>{errors}</strong>
            </div>

            <div className="pattern-matrices-result-card">
              <span>Precisión</span>
              <strong>
                {Math.round(accuracy)}%
              </strong>
            </div>

            <div className="pattern-matrices-result-card">
              <span>Series completadas</span>
              <strong>{totalAnswers}</strong>
            </div>
          </div>

          <GameInterpretation
            interpretation={interpretation}
          />

          <button
            type="button"
            className="pattern-matrices-restart"
            onClick={() =>
              startGame(
                selectedDifficulty || "easy"
              )
            }
          >
            Jugar de nuevo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pattern-matrices-page">
      <main className="pattern-matrices-card">
        <header className="pattern-matrices-header">
          <div>
            <p className="pattern-matrices-area">
              Razonamiento
            </p>

            <h1>Matrices y series</h1>

            <p>
              Elige el elemento que continúa
              correctamente la secuencia.
            </p>
          </div>

          <div className="pattern-matrices-score">
            <span>Aciertos</span>
            <strong>{correctAnswers}</strong>
          </div>
        </header>

        <GameProgress
          currentRound={roundIndex + 1}
          totalRounds={totalRounds}
          progress={progress}
        />

        <section className="pattern-matrices-game">
          <div className="pattern-matrices-sequence">
            {round?.sequence.map(
              (token, index) => (
                <div
                  key={`${round.id}-sequence-${index}`}
                  className="pattern-matrices-sequence-item"
                >
                  <PatternToken token={token} />
                </div>
              )
            )}

            <div className="pattern-matrices-sequence-item pattern-matrices-missing">
              ?
            </div>
          </div>

          <div className="pattern-matrices-divider">
            <span />
            <strong>
              ¿Qué elemento falta?
            </strong>
            <span />
          </div>

          <div
            className="pattern-matrices-options"
            style={{
              gridTemplateColumns: `repeat(${Math.min(
                round?.options.length || 2,
                5
              )}, minmax(95px, 1fr))`,
            }}
          >
            {round?.options.map(
              (option, index) => (
                <button
                  type="button"
                  key={option.id}
                  className="pattern-matrices-option"
                  onClick={() =>
                    handleAnswer(option)
                  }
                  disabled={responseLocked}
                  aria-label={`Opción ${
                    index + 1
                  }`}
                >
                  <span className="pattern-matrices-option-number">
                    {index + 1}
                  </span>

                  <PatternToken
                    token={option}
                    compact
                  />
                </button>
              )
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

export default PatternMatrices;